package nl.staartvin.solarpower.data.uv

/**
 * Fitzpatrick skin phototypes. The standard medical/dermatology classification
 * for how skin responds to UV exposure — used here to scale the "safe time in
 * sun before first reddening" calculation.
 *
 * [medFactor] is a multiplier on the Type II baseline MED (minimal erythemal
 * dose). Empirical values from dermatology literature:
 *   - Type I tolerates ~0.67× the Type II dose.
 *   - Type II is the baseline (1.0×).
 *   - Type III ~1.5×, Type IV ~2.0×, Type V ~3.5×, Type VI ~6×.
 *
 * These are population averages; individual variation is significant. The
 * UI surfaces a brief disclaimer.
 */
enum class SkinType(
    val romanNumeral: String,
    val shortName: String,
    val description: String,
    val medFactor: Double,
) {
    Type1("I", "Very fair", "Always burns, never tans", 0.67),
    Type2("II", "Fair", "Usually burns, tans minimally", 1.0),
    Type3("III", "Light", "Sometimes burns, tans gradually", 1.5),
    Type4("IV", "Olive", "Rarely burns, tans easily", 2.0),
    Type5("V", "Brown", "Very rarely burns, tans deeply", 3.5),
    Type6("VI", "Dark brown / black", "Never burns", 6.0),
    ;

    companion object {
        val Default = Type2  // Type II is the standard reference; broad NL audience match.

        /** All cases as a List — used from Swift, where `entries` doesn't bridge cleanly. */
        val all: List<SkinType> = listOf(Type1, Type2, Type3, Type4, Type5, Type6)

        fun fromName(name: String?): SkinType =
            all.firstOrNull { it.name == name } ?: Default
    }
}
